import { ReactNode, useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInfoBox from "../../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import "./reset-changes-popover.scss";

interface ResetChangesPopoverProps {
  onReset: () => void;
  section: string;
  children?: ReactNode;
  showIndicator?: boolean;
  // KOA-15838: render the dot in the BLUE token colour (vs the default orange
  // "locally changed") — used while a style tag is being edited, where the
  // values shown belong to the tag (a token), not a local DI override.
  tokenColor?: boolean;
}

const ResetChangesPopover = ({
  onReset,
  section,
  children,
  showIndicator = true,
  tokenColor = false
}: ResetChangesPopoverProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleVisibleChange = (visible: boolean) => {
    setIsVisible(visible);
  };

  const handleReset = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (typeof onReset === "function") {
      onReset();
    }

    setIsVisible(false);
  };

  return (
    <KuikaPopover
      visible={isVisible}
      onVisibleChange={handleVisibleChange}
      title={
        <div
          className="reset-changes-popover__title"
          onClick={handleReset}
          data-testid={`reset-changes-${section}-confirm`}
        >
          <KuikaButton type="tertiary" size="sm" isIcon color="neutral">
            <KuikaSvg name="arrow-back-up" width={16} height={16} type="arrows" />
          </KuikaButton>
          <KuikaText>Reset changes</KuikaText>
        </div>
      }
      content={
        <KuikaInfoBox
          leftUnit={<KuikaSvg name="info-circle" width={16} height={16} />}
          rightUnit={<KuikaSmallText>Resetting will revert to the initial value</KuikaSmallText>}
        />
      }
      position="top"
      className="reset-changes-popover"
    >
      {children ? (
        <div
          className="reset-changes-popover__custom-trigger"
          data-testid={`reset-changes-${section}-trigger`}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsVisible(true);
          }}
        >
          {children}
        </div>
      ) : showIndicator ? (
        <div
          className="panel-change-indicator__container"
          data-testid={`reset-changes-${section}-indicator`}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsVisible(true);
          }}
        >
          <KuikaTooltip content={`Custom style added`} position="bottom">
            <span
              className={tokenColor ? "panel-change-indicator panel-change-indicator--token" : "panel-change-indicator"}
              tabIndex={0}
              aria-label={`Reset ${section} changes`}
            />
          </KuikaTooltip>
        </div>
      ) : null}
    </KuikaPopover>
  );
};

export default ResetChangesPopover;
